
import React, { useEffect, useMemo, useState } from 'react';
import './styles/Booking.css';
import Footer from '../home/components/Footer';
import Shapes from '../../components/Shapes';
import Slot from './components/Slot';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { dateToString } from '../../utils/Date';
import { db } from "../../services/Firebase"
import EdakkalDay from '../../models/EdakkalDay';
import axios from 'axios';
import BookingSuccess from './components/Success';
import Config from '../../config/Config';
import LoadingPage from '../../components/Loading';

function loadScript(src) {
    return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => {
            resolve(true);
        }
        script.onerror = () => {
            resolve(false);
        }
        document.body.appendChild(script);
    })
}

function NavigationBar() {
    return (
        <nav id="navbar" style={{ backgroundColor: 'black', color: 'white', padding: '10px' }}>
            <h1>Book Tickets: Edakkal Caves</h1>
        </nav>
    );

}

/**
 * This is the booking page.
 * 
 * @returns The booking page
 */
function Booking() {
    // Some states
    let [date, setDate] = useState(new Date());
    let [day, setDay] = useState(null);
    let [selection, setSelection] = useState({
        slot: "Early Bird",
        adults: 0,
        children: 0,
        camera: false,
    });
    let [errors, setErrors] = useState([]);
    let [disabled, setDisabled] = useState({
        "Early Bird": false,
        "Morning": false,
        "Afternoon": false,
    });
    let [completeData, setFinished] = useState({
        finished: false,
        loading: false,
    });
    let [isButtonDisabled, setButtonDisabled] = useState(false);

    // Slot names and their corresponding keys in the day object
    const slotMap = useMemo(() => {
        return {
            "Early Bird": "earlyBird",
            "Morning": "morning",
            "Afternoon": "afternoon",
        };
    }, [])

    /**
     * This method is called when the user clicks on the Book Now button.
     * It creates an order and calls the pay method.
     * 
     * @param {number} amount The amount to be paid in Rupees
     * @returns The order object
     */
    const createOrder = async (amount) => {
        try {
            const order = await axios.post(Config.API_URL, {
                amount: amount * 100,
                currency: "INR",
            })
            return order;
        } catch (e) {
            return null;
        }
    }

    /**
     * This method is a wrapper around the pay method.
     * It checks if all the fields are filled and valid.
     * If not, it alerts the user.
     * 
     * If all the fields are valid, it calls the pay method.
     *
     * @see pay
     * @returns nothing
     */
    const beforePay = async () => {
        if (isButtonDisabled) {
            return;
        }
        setButtonDisabled(true);

        if (disabled[selection.slot]) {
            alert("Unable to book tickets for this slot. Please select another slot.");
            return;
        }

        const name = document.getElementById('name-field').value;
        const phone = document.getElementById('phone-field').value;
        const email = document.getElementById('email-field').value;
        if (!name || !phone || !email) {
            alert("Please fill your name, phone number and email!");
            return;
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(email)) {
            alert("Please enter a valid email address!");
            return;
        }

        const phoneRegex = /^(\+\d{1,3}[- ]?)?\d{10}$|^\(\d{3}\)\s*\d{3}[- ]?\d{4}$/;
        if (!phoneRegex.test(phone)) {
            alert("Please enter a valid phone number!");
            return;
        }


        const adults = document.getElementById('adults-field').value;
        const children = document.getElementById('children-field').value;
        const camera = document.getElementById('camera-checkbox').checked;
        let counts = {};

        try {
            counts = {
                adults: parseInt(adults),
                children: parseInt(children),
                camera: camera ? 1 : 0,
            }
            if (isNaN(counts.adults)) {
                throw new Error();
            }

            if (counts.adults <= 0 || counts.children < 0) {
                throw new Error();
            }
        } catch (e) {
            alert("Please enter valid numbers for adults and children!");
            return;
        }

        const slotDetail = day?.[slotMap[selection.slot]];

        if (!slotDetail) {
            alert("Please select a slot!");
            return;
        }

        if (counts.adults > slotDetail.slotsLeft) {
            alert(`Only ${slotDetail.slotsLeft} slots left for this slot!`);
            return;
        }

        const amount = (counts.camera * slotDetail.videoCameraPrice) + (counts.adults * slotDetail.adultPrice) + (counts.children * slotDetail.childPrice);
        if (amount <= 0) {
            alert("Please select at least one ticket!");
            return;
        }

        const obj = {
            name,
            phone,
            email,
            amount,
            slot: selection.slot,
            counts,
            date: dateToString(date),
        }

        try {
            await pay(obj);
        } catch (e) {
            alert("Unable to book tickets. Please try again later.");
        } finally {
            setButtonDisabled(false);
        }
    }

    /**
     * This method is called when the user clicks on the Book Now button and all the fields are valid.
     * It loads the Razorpay SDK and creates an order.
     * 
     * It then opens the Razorpay payment dialog.
     * 
     * @param {Object} obj The object containing the booking details
     * @returns nothing
     */
    const pay = async (obj) => {
        const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
        if (!res) {
            alert("Razorpay SDK failed to load. Are you online?");
            return;
        }
        const o = await createOrder(obj.amount);
        if (!o) {
            alert("Error creating order!");
            return;
        }
        const options = {
            key: "rzp_test_2GjxjO4HeIbJ4J",
            amount: obj.amount,
            currency: "INR",
            name: "DTPC Wayanad",
            description: "Edakkal Caves Ticket Booking",
            image: "https://www.keralatourism.org/images/dtpc.png",
            order_id: o.data.id,
            handler: async function (response) {
                const payment = {
                    paymentId: response.razorpay_payment_id,
                    orderId: response.razorpay_order_id,
                    signature: response.razorpay_signature,
                }
                obj.payment = payment;
                obj.slotDetails = day?.[slotMap[obj.slot]];

                // set loading
                setFinished({ finished: false, loading: true });
                const res = await axios.post(`${Config.API_URL}/verify`, obj);
                setFinished({ loading: false });

                obj.bookingId = res.data.bookingId;
                obj.bookingTimestamp = res.data.bookingTimestamp;
                obj.bookingDate = res.data.bookingDate;
                if (res.data.success) {
                    alert("Payment successful!");
                    setFinished({ finished: true, ...obj, });
                } else {
                    alert("Payment failed!");
                }
                setButtonDisabled(false);
            },
            prefill: {
                name: obj.name,
                email: obj.email,
                contact: obj.phone,
            },
        };
        const paymentObject = new window.Razorpay(options);
        paymentObject.open();
    }


    useEffect(() => {
        if (!completeData.finished && !completeData.loading) {
            const dateField = document.getElementById('date-field');
            dateField.valueAsDate = date;
        }
        const dateStr = dateToString(date);
        const q = doc(db, `daySlots/${dateStr}`);
        return onSnapshot(q, async (snap) => {
            if (snap.exists()) {
                const d = EdakkalDay.fromJson(snap.data());
                setDay(d);
                if (!d.isAvailable) {
                    setErrors([`The selected day (${dateStr}) is not available.`]);
                }
            } else {
                setDay(EdakkalDay.defaultDay);
                try {
                    await setDoc(q, EdakkalDay.defaultDay.toJson());
                } catch (e) {
                    console.error(e);
                }
            }
        });

    }, [date, completeData.finished, completeData.loading]);

    useEffect(() => {
        /**
        * This method returns `true` if the slot is disabled.
        * 
        * A slot is disabled if:
        * 1. The day is not available (holiday, or disabled by admin)
        * 2. The slot is not available (disabled by admin)
        * 3. The slot is today and the the slot time is already over
        * 4. The date is not between today and one month from now
        * 5. There are no slots left for the slot
        * 6. There are not enough slots left for the number of adults
        * 7. The date is a Monday
        * 8. The date is August 15
        * 
        * @param {string} slot 
        * @returns {boolean} `true` if the slot is disabled
        */
        const isSlotDisabled = (slot) => {
            const now = new Date();
            const daySlot = day?.[slotMap[slot]];
            if (!daySlot) {
                return true;
            }
            const t = now.getHours() * 60 + now.getMinutes();

            const dayIsNotAvailable = day?.isAvailable === false;
            const daySlotIsNotAvailable = daySlot?.isAvailable === false;
            const todayAndTimeOver = t > daySlot.startTime && dateToString(now) === dateToString(date);
            const betweenOneMonth = checkBetweenOneMonth(date);
            const slotsLeftIsZero = daySlot?.slotsLeft === 0;
            const thereAreEnoughSlots = daySlot?.slotsLeft > selection.adults;
            const isMonday = date.getDay() === 1;
            const isAugust15 = date.getDate() === 15 && date.getMonth() === 7;

            if (isMonday) {
                setErrors(["Edakkal Caves is closed on Mondays."]);
            }

            return dayIsNotAvailable || daySlotIsNotAvailable || todayAndTimeOver || !betweenOneMonth || slotsLeftIsZero || !thereAreEnoughSlots || isMonday || isAugust15;
        }

        setDisabled({
            "Early Bird": isSlotDisabled("Early Bird"),
            "Morning": isSlotDisabled("Morning"),
            "Afternoon": isSlotDisabled("Afternoon"),
        });
    }, [selection.slot, date, selection.adults, slotMap, day]);


    /**
     * This method is called when a slot is selected.
     * It sets the slot state.
     */
    const selectSlot = (slot) => {
        setSelection({ ...selection, slot });
    }

    /**
     * This method calculates the amount to be paid.
     * 
     * @returns The amount to be paid as a string
     */
    const calculateAmount = () => {
        if (disabled[selection.slot]) {
            return "";
        }
        const slotDetail = day?.[slotMap[selection.slot]];
        if (!slotDetail) {
            return "";
        }
        const amount = (selection.camera * slotDetail.videoCameraPrice) + (selection.adults * slotDetail.adultPrice) + (selection.children * slotDetail.childPrice);
        return amount <= 0 ? "" : `₹${amount}`;
    }

    /**
     * Returns `true` if the date is between today and one month from now.
     * Returns flase otherwise (the date is before today or after one month from now) and sets the errors.
     * 
     * @param {Date} date The date to be checked
     * @returns 
     */
    const checkBetweenOneMonth = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const oneMonthAhead = new Date();
        oneMonthAhead.setMonth(today.getMonth() + 1);
        if (date <= today || date > oneMonthAhead) {
            setErrors(["Please select a date between today and one month from now."]);
            return false;
        }
        return true;
    }

    /**
     * This method is called when the date is changed in the date field.
     * It checks if the date is between today and one month from now.
     * If not, it sets the errors.
     * 
     * If the date is valid, it sets the date state.
     * 
     * @param {HTMLInputElement} e 
     * @returns 
     */
    const handleDateChange = async (e) => {
        setErrors([]);

        const selectedDate = e.target.valueAsDate;
        if (!selectedDate) {
            return;
        }

        const r = checkBetweenOneMonth(selectedDate);
        if (!r) {
            return;
        }

        setDate(selectedDate);
    };

    /**
     * This method returns the classes for the error container.
     * If there are errors, it returns `error show`.
     * If there are no errors, it returns `error hidden`.
     * 
     * @returns The classes for the error container
     */
    const errorClasses = () => {
        let c = "error";
        if (errors.length > 0) {
            c += " show";
        } else {
            c += " hidden";
        }
        return c;
    }

    /**
     * This method is used to get the minimum date that can be selected. Of course, it is today.
     * @returns The minimum date that can be selected
     */
    const minDate = () => {
        return new Date().toISOString().split('T')[0];
    }

    /**
     * This method is used to get the maximum date that can be selected. It is one month from now.
     * @returns The maximum date that can be selected
     * @see minDate
     */
    const maxDate = () => {
        const d = new Date();
        d.setMonth(d.getMonth() + 1);
        return d.toISOString().split('T')[0];
    }

    /**
     * This method returns the classes for the submit button.
     */
    const submitClasses = () => {
        let c = "submit";
        if (isButtonDisabled) {
            c += " disabled";
        }
        return c;
    }

    if (completeData.loading) {
        return (
            <LoadingPage
                text={"Your payment is processing"}
                caption={"Just a moment, we're making things happen..."}
            />
        );
    }

    if (completeData.finished) {
        return (
            <BookingSuccess
                details={completeData}
            />
        );
    }

    return (
        <div>
            <NavigationBar />
            <div className='booking-page'>
                <div className='form'>
                    <div className={errorClasses()} id='error-container'>
                        <p id='error'>
                            {errors.map((e) => (
                                <span key={Math.random()}>- {e} <br /></span>
                            ))}
                        </p>
                    </div>
                    <input type="date" min={minDate()} max={maxDate()} onChange={handleDateChange} id='date-field' />
                    <label>
                        Select a slot
                    </label>
                    <div className='slot-selection-details'>
                        {Object.keys(slotMap).map((slot) => (
                            <Slot
                                key={slot}
                                time={slot}
                                slotsLeft={day?.[slotMap[slot]]?.slotsLeft || 0}
                                selected={selection.slot === slot}
                                onClick={selectSlot}
                                disabled={disabled[slot]}
                            />
                        ))}
                    </div>
                    <div className='pricing-table'>
                        <table>
                            <thead>
                                <tr>
                                    <td colSpan={2}>Timing: {day?.[slotMap[selection.slot]]?.time}</td>
                                </tr>
                            </thead>
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Adult</td>
                                    <td>₹{day?.[slotMap[selection.slot]]?.adultPrice || 0}</td>
                                </tr>
                                <tr>
                                    <td>Child (Age 5 to 12)</td>
                                    <td>₹{day?.[slotMap[selection.slot]]?.childPrice || 0}</td>
                                </tr>
                                <tr>
                                    <td>Video Camera</td>
                                    <td>₹{day?.[slotMap[selection.slot]]?.videoCameraPrice || 0}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <input type="number" placeholder="Number of adults" id='adults-field' max={10} min={1} onChange={
                        (e) => {
                            const val = parseInt(e.target.value);
                            if (isNaN(val) || val < 0) {
                                e.target.value = "";
                            }

                            if (val > 10) {
                                e.target.value = "10";
                                alert("You can book a maximum of 10 adults tickets at a time!");
                            }

                            if (val > day?.[slotMap[selection.slot]]?.slotsLeft) {
                                e.target.value = day?.[slotMap[selection.slot]]?.slotsLeft || 0;
                                alert(`Only ${day?.[slotMap[selection.slot]]?.slotsLeft} slots left for this slot!`);
                            }

                            setSelection({ ...selection, adults: isNaN(val) ? 0 : val })
                        }
                    } />
                    <input type="number" placeholder="Number of children" id='children-field' min={0} max={10} onChange={
                        (e) => {
                            const val = parseInt(e.target.value);
                            if (isNaN(val) || val < 0) {
                                e.target.value = "";
                            }

                            if (val > 10) {
                                e.target.value = "10";
                                alert("You can book a maximum of 10 children tickets at a time!");
                            }

                            setSelection({ ...selection, children: isNaN(val) ? 0 : val })
                        }

                    } />
                    <label>
                        <input type="checkbox" id='camera-checkbox' onChange={
                            (e) => setSelection({ ...selection, camera: e.target.checked })
                        } />
                        Bringing a camera
                    </label>
                    <input type="text" placeholder="Name" id='name-field' />
                    <input type="tel" placeholder="Phone number" id='phone-field' />
                    <input type="email" placeholder="Email" id='email-field' />
                    <button type="submit" onClick={beforePay} className={submitClasses()}>Book Now {calculateAmount()} </button>
                </div>
            </div>
            <Shapes />
            <Footer />
        </div>
    );
}

export default Booking;

export { NavigationBar };